import type {NexusHandler} from "@otto-ec/global-resources/nexus";
import {type ThirdpartyTracking, thirdpartyTracking, type TrackFirebaseResult,} from "./index.js";
import {AppType, appType, supportedAppVersion} from "../global/appInfo.js";
import {APP_VERSION_REQUIREMENTS} from "../appVersionRequirements.js";
import type {AdjustPayload} from "./adjustPayload.js";
import type {FirebasePayload} from "./firebasePayload.js";
import {getAdjustTrackingConsent} from "@otto-ec/eprivacy-cmp";
import {firebasePayloadFromObject} from "./types/firebasePayloadValidation.js";
import {shouldDisplaySmartAppBannerWithTracking} from "./smartbanner/smartAppBanner.js";

export const trackAdjust: NexusHandler<ThirdpartyTracking["trackAdjust"]> = async function (adjustPayload: AdjustPayload) {
    if (!await isAdjustTrackingAllowed()) {
        return;
    }
    const type = appType();
    if (type === AppType.ANDROID) {
        const androidAdjustPayload = JSON.stringify(adjustPayload);
        window.o_apps_android?.trackAdjust?.(androidAdjustPayload);
    } else if (type === AppType.IOS) {
        window.webkit?.messageHandlers?.trackAdjust?.postMessage(adjustPayload);
    }
};

async function isAdjustTrackingAllowed() {
    return await supportedAppVersion(APP_VERSION_REQUIREMENTS.adjustNativeConsent) ?
        true : await getAdjustTrackingConsent()
}

export const trackFirebase: NexusHandler<ThirdpartyTracking["trackFirebase"]> = async function (
    firebasePayload: FirebasePayload,
): Promise<TrackFirebaseResult> {
    const parsedPayload = await firebasePayloadFromObject(firebasePayload);
    if ("validation_error" in parsedPayload) {
        return parsedPayload;
    }
    const validPayload = parsedPayload.data;
    const isSingleItemPayload = !("items" in validPayload.event_params);

    const type = appType();
    const isAndroid = type === AppType.ANDROID;
    const isSupported = isAndroid && await isSupportedAppVersion();
    if (isAndroid && !isSupported && !isSingleItemPayload) {
        return androidCompatibilityError();
    }

    if (isAndroid && (isSupported || isSingleItemPayload)) {
        const androidFirebasePayload = JSON.stringify(validPayload);
        window.o_apps_android?.trackFirebase?.(androidFirebasePayload);
    }

    if (type === AppType.IOS) {
        window.webkit?.messageHandlers?.trackFirebase?.postMessage(validPayload);
    }

    return { success: true };
};

async function isSupportedAppVersion(): Promise<boolean> {
    return supportedAppVersion(APP_VERSION_REQUIREMENTS.trackFirebaseItemsArray);
}

function androidCompatibilityError(): TrackFirebaseResult {
    return {
        validation_error: {
            compatibility_error: `Android App versions older than ${APP_VERSION_REQUIREMENTS.trackFirebaseItemsArray.android} do not support items array payload.`
        }
    };
}

thirdpartyTracking.assignNamespace({trackAdjust, trackFirebase, shouldDisplaySmartAppBannerWithTracking});
